import React, { useMemo, useState, useRef } from 'react';
import { ArrowLeft, Info, ChevronLeft, ChevronRight, Snowflake, GitBranch, Compass } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import {
  Container, NavOverlay, NavButton, TooltipBox,
  EdgeArrow, PivotCounter, ZoomControlsPanel, ZoomButton, ZoomLevel,
  SvgDefs, useZoomToFit, getAdaptiveTextParams,
  DEFAULT_LINE_WIDTH, wrapText, getScenarioColor,
  buildGradientStops, estimateLabelHeight,
} from './branchViewShared';

/**
 * Tree layout: Center (pivot) → Scenario → Variable (vaikutus) → Member leaves
 * Follows the actual tree structure of the data.
 */
export default function PivotPointBranchViewTree({
  pivotPoint,
  memberNodes,
  scenarios,
  width,
  height,
  onBack,
  onInfoClick,
  onMemberClick,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  layoutMode,
  onToggleLayout,
}) {
  const { t } = useTranslation();
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const touchTooltipClearRef = useRef(null);

  // Scenario lookup
  const scenarioById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => { map[s.id] = s; });
    return map;
  }, [scenarios]);

  const scheduleTooltipClear = () => {
    if (touchTooltipClearRef.current) clearTimeout(touchTooltipClearRef.current);
    touchTooltipClearRef.current = setTimeout(() => {
      touchTooltipClearRef.current = null;
      setHoveredNodeId(null);
      setTooltip(null);
    }, 2200);
  };

  const showLeafTooltipFromClient = (leaf, clientX, clientY) => {
    const scenarioName = scenarioById[leaf.scenarioId]?.title || '';
    setHoveredNodeId(leaf.id);
    setTooltip({
      x: clientX + 12,
      y: clientY - 10,
      title: leaf.text || leaf.title || '',
      scenarioName,
      scenarioColor: getScenarioColor(scenarioName),
      participant: leaf.participant,
    });
  };

  // Variable lookup from scenarios
  const variableById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => {
      (s.variables || []).forEach(v => {
        map[v.id] = { ...v, scenarioId: s.id, scenarioTitle: s.title, color: s.color };
      });
    });
    return map;
  }, [scenarios]);

  // Group: scenario → variable → members
  const treeData = useMemo(() => {
    const scenarioMap = new Map(); // scenarioId → { variables: Map<varId, members[]> }

    (memberNodes || []).forEach(member => {
      const sId = member.scenarioId || 'unknown';
      if (!scenarioMap.has(sId)) {
        scenarioMap.set(sId, { scenarioId: sId, variables: new Map() });
      }
      const varId = member.connectedVariables?.[0] || 'unknown';
      const vars = scenarioMap.get(sId).variables;
      if (!vars.has(varId)) {
        vars.set(varId, []);
      }
      vars.get(varId).push(member);
    });

    // Convert to array structure
    return Array.from(scenarioMap.entries()).map(([sId, data]) => {
      const scenario = scenarioById[sId];
      return {
        scenarioId: sId,
        title: scenario?.title || sId,
        color: scenario?.color || getScenarioColor(scenario?.title) || '#607d8b',
        variables: Array.from(data.variables.entries()).map(([vId, members]) => {
          const varInfo = variableById[vId];
          return {
            variableId: vId,
            title: varInfo?.title || 'Tuntematon',
            members,
          };
        }),
      };
    });
  }, [memberNodes, scenarioById, variableById]);

  // Layout: 4 levels radially
  const layout = useMemo(() => {
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    const scenarioRadius = minDim * 0.15;
    const variableRadius = minDim * 0.32;
    const baseLeafRadius = minDim * 0.56;

    const scenarioCount = treeData.length;
    const centerRadius = Math.max(32, minDim * 0.04);
    const scenarioNodeRadius = Math.max(18, minDim * 0.022);
    const variableNodeRadius = Math.max(12, minDim * 0.016);
    const leafNodeRadius = Math.max(7, minDim * 0.01);

    // Count total variables for global angle distribution
    const allVariables = treeData.flatMap(s => s.variables);
    const totalVarCount = allVariables.length;

    const branches = treeData.map((scenarioGroup, sIdx) => {
      // Each scenario gets a slice of the circle proportional to its variable count
      const scenarioAngle = scenarioCount === 1
        ? -Math.PI / 2
        : (sIdx / scenarioCount) * 2 * Math.PI - Math.PI / 2;

      const sx = cx + Math.cos(scenarioAngle) * scenarioRadius;
      const sy = cy + Math.sin(scenarioAngle) * scenarioRadius;

      const scenarioFanAngle = scenarioCount === 1
        ? Math.PI * 1.6
        : ((2 * Math.PI) / scenarioCount) * 0.8;

      const varCount = scenarioGroup.variables.length;

      // Distribute variables within the scenario fan
      const children = scenarioGroup.variables.map((varGroup, vIdx) => {
        const varAngle = varCount === 1
          ? scenarioAngle
          : scenarioAngle - scenarioFanAngle / 2 + (vIdx / (varCount - 1)) * scenarioFanAngle;

        const vx = cx + Math.cos(varAngle) * variableRadius;
        const vy = cy + Math.sin(varAngle) * variableRadius;

        const memberCount = varGroup.members.length;

        // Sub-fan for leaves within this variable
        const maxLeafFan = varCount === 1
          ? scenarioFanAngle * 0.8
          : (scenarioFanAngle / varCount) * 0.85;

        // Anti-overlap: compute needed angle from label heights
        const maxLabelH = Math.max(
          ...varGroup.members.map(m => estimateLabelHeight(m.text)),
          35
        ) * 1.22;
        const minStep = maxLabelH / baseLeafRadius;
        const neededAngle = memberCount > 1 ? minStep * (memberCount - 1) : 0;

        let leafRadius = baseLeafRadius;
        let usedFan = maxLeafFan;
        if (neededAngle > maxLeafFan && memberCount > 1) {
          leafRadius = (maxLabelH * (memberCount - 1)) / maxLeafFan;
          leafRadius = Math.min(leafRadius, minDim * 0.85);
        } else {
          usedFan = neededAngle > 0 ? neededAngle : maxLeafFan;
        }

        const leafStep = memberCount > 1 ? usedFan / (memberCount - 1) : 0;
        const leafStart = varAngle - usedFan / 2;
        const stagger = memberCount > 5;
        const staggerOffset = leafRadius * 0.12;

        const leaves = varGroup.members.map((member, mIdx) => {
          const leafAngle = memberCount === 1 ? varAngle : leafStart + mIdx * leafStep;
          const r = stagger ? leafRadius + (mIdx % 2 === 0 ? 0 : staggerOffset) : leafRadius;
          return {
            ...member,
            x: cx + Math.cos(leafAngle) * r,
            y: cy + Math.sin(leafAngle) * r,
            nodeRadius: leafNodeRadius,
          };
        });

        return {
          variableId: varGroup.variableId,
          title: varGroup.title,
          x: vx,
          y: vy,
          angle: varAngle,
          nodeRadius: variableNodeRadius,
          leaves,
          memberCount,
          color: scenarioGroup.color,
        };
      });

      const totalMembers = scenarioGroup.variables.reduce((sum, v) => sum + v.members.length, 0);

      return {
        ...scenarioGroup,
        x: sx,
        y: sy,
        angle: scenarioAngle,
        nodeRadius: scenarioNodeRadius,
        children,
        totalMembers,
      };
    });

    return { cx, cy, centerRadius, branches };
  }, [width, height, treeData]);

  // Center gradient
  const centerGradientStops = useMemo(() => {
    const names = pivotPoint?.scenarioNames || [];
    return names.length >= 2 ? buildGradientStops(names) : null;
  }, [pivotPoint]);

  const centerFill = useMemo(() => {
    if (centerGradientStops) return 'url(#center-gradient)';
    const names = pivotPoint?.scenarioNames || [];
    if (names.length === 1) return getScenarioColor(names[0]);
    return '#9C27B0';
  }, [pivotPoint, centerGradientStops]);

  // Zoom
  const { svgRef, zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, zoomToPoints } =
    useZoomToFit({ pivotId: pivotPoint?.id, layout, width, height });

  // Adaptive text — shared across all branch views
  const totalLeaves = layout.branches.reduce((s, b) => s + b.children.reduce((s2, v) => s2 + v.leaves.length, 0), 0);
  const tp = getAdaptiveTextParams(totalLeaves, zoomLevel);

  // Hover
  const isHighlighted = (nodeId) => !hoveredNodeId || nodeId === hoveredNodeId;
  const dimOpacity = hoveredNodeId ? 0.15 : 1;

  const handleMouseEnter = (node, event) => {
    showLeafTooltipFromClient(node, event.clientX, event.clientY);
  };
  const handleMouseLeave = () => { setHoveredNodeId(null); setTooltip(null); };

  const handleLeafTouchStart = (leaf, e) => {
    const t = e.touches?.[0];
    if (!t) return;
    e.stopPropagation();
    if (touchTooltipClearRef.current) clearTimeout(touchTooltipClearRef.current);
    touchTooltipClearRef.current = null;
    showLeafTooltipFromClient(leaf, t.clientX, t.clientY);
  };

  const handleLeafTouchEnd = () => {
    scheduleTooltipClear();
  };

  return (
    <Container>
      <NavOverlay>
        {onBack && (
          <NavButton onClick={onBack}><ArrowLeft /> {t('pivotPoints.common.back')}</NavButton>
        )}
        <NavButton onClick={onInfoClick}><Info /> {t('pivotPoints.common.info')}</NavButton>
        {onToggleLayout && (
          <NavButton onClick={onToggleLayout} title={t('pivotPoints.branch.cardinalTitle')}>
            <Compass /> {t('pivotPoints.branch.cardinal')}
          </NavButton>
        )}
      </NavOverlay>

      {totalCount > 1 && (
        <>
          <EdgeArrow $side="left" onClick={onPrev} disabled={currentIndex <= 0} title={t('pivotPoints.common.previousPivot')}>
            <ChevronLeft />
          </EdgeArrow>
          <EdgeArrow $side="right" onClick={onNext} disabled={currentIndex >= totalCount - 1} title={t('pivotPoints.common.nextPivot')}>
            <ChevronRight />
          </EdgeArrow>
          <PivotCounter>{currentIndex + 1} / {totalCount}</PivotCounter>
        </>
      )}

      <svg ref={svgRef} width={width} height={height}>
        <SvgDefs centerGradientStops={centerGradientStops} />
        <g className="zoomable-content">

        {/* Lines: center → scenario */}
        {layout.branches.map((branch, i) => (
          <line key={`cs-${i}`} x1={layout.cx} y1={layout.cy} x2={branch.x} y2={branch.y}
            stroke={branch.color} strokeWidth={2.5} strokeOpacity={hoveredNodeId ? 0.1 : 0.35} />
        ))}

        {/* Lines: scenario → variable */}
        {layout.branches.map((branch, bi) =>
          branch.children.map((varNode, vi) => (
            <line key={`sv-${bi}-${vi}`} x1={branch.x} y1={branch.y} x2={varNode.x} y2={varNode.y}
              stroke={branch.color} strokeWidth={1.8} strokeOpacity={hoveredNodeId ? 0.08 : 0.25} />
          ))
        )}

        {/* Lines: variable → leaves */}
        {layout.branches.map((branch, bi) =>
          branch.children.map((varNode, vi) =>
            varNode.leaves.map((leaf, li) => (
              <line key={`vl-${bi}-${vi}-${li}`}
                x1={varNode.x} y1={varNode.y} x2={leaf.x} y2={leaf.y}
                stroke={branch.color} strokeWidth={1}
                strokeOpacity={isHighlighted(leaf.id) ? 0.35 : dimOpacity * 0.15}
                style={{ transition: 'stroke-opacity 0.2s ease' }} />
            ))
          )
        )}

        {/* Leaf nodes — adaptive text */}
        {layout.branches.map((branch, bi) =>
          branch.children.map((varNode, vi) =>
            varNode.leaves.map((leaf, li) => {
              const highlighted = isHighlighted(leaf.id);
              const { lines } = wrapText(leaf.text || '', tp.labelWidth, tp.maxLabelLines);
              const labelY = leaf.y + leaf.nodeRadius + 18;
              return (
                <g key={`leaf-${bi}-${vi}-${li}`}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                  opacity={highlighted ? 1 : dimOpacity}
                  onMouseEnter={(e) => handleMouseEnter(leaf, e.nativeEvent)}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={(e) => handleLeafTouchStart(leaf, e)}
                  onTouchEnd={handleLeafTouchEnd}
                  onClick={() => onMemberClick?.(leaf)}>
                  <circle cx={leaf.x} cy={leaf.y} r={leaf.nodeRadius}
                    fill={branch.color} fillOpacity={0.7}
                    stroke={highlighted && hoveredNodeId ? 'white' : 'rgba(255,255,255,0.3)'}
                    strokeWidth={highlighted && hoveredNodeId ? 1.5 : 0.5}
                    filter={highlighted && hoveredNodeId ? 'url(#leaf-glow)' : undefined} />
                  <text x={leaf.x} y={labelY} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize={`${tp.leafFontSize}px`} fontWeight="500">
                    {lines.map((line, idx) => (
                      <tspan key={idx} x={leaf.x} dy={idx === 0 ? 0 : tp.lineHeight}>{line}</tspan>
                    ))}
                  </text>
                </g>
              );
            })
          )
        )}

        {/* Variable nodes — click to zoom to this variable's leaves */}
        {layout.branches.map((branch, bi) =>
          branch.children.map((varNode, vi) => {
            const { lines } = wrapText(varNode.title, 18, 3);
            const labelY = varNode.y + varNode.nodeRadius + 18;
            const handleVarClick = () => {
              const points = [
                { x: varNode.x - 30, y: varNode.y - 30 },
                { x: varNode.x + 30, y: varNode.y + 30 },
                ...varNode.leaves.map(l => ({ x: l.x - 60, y: l.y - 20 })),
                ...varNode.leaves.map(l => ({ x: l.x + 60, y: l.y + 40 })),
              ];
              zoomToPoints(points, `var-${bi}-${vi}`);
            };
            return (
              <g key={`var-${bi}-${vi}`} style={{ cursor: 'pointer' }} onClick={handleVarClick}>
                <circle cx={varNode.x} cy={varNode.y} r={varNode.nodeRadius}
                  fill={branch.color} fillOpacity={0.7}
                  stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
                <text x={varNode.x} y={varNode.y} textAnchor="middle" dominantBaseline="central"
                  fill="white" fontSize={Math.max(9, varNode.nodeRadius * 0.55) + 'px'} fontWeight="700">
                  {varNode.memberCount}
                </text>
                <text x={varNode.x} y={labelY} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="10px" fontWeight="600">
                  {lines.map((line, idx) => (
                    <tspan key={idx} x={varNode.x} dy={idx === 0 ? 0 : 12}>{line}</tspan>
                  ))}
                </text>
              </g>
            );
          })
        )}

        {/* Scenario branch nodes — click to zoom to all children */}
        {layout.branches.map((branch, i) => {
          const { lines } = wrapText(branch.title, 16, 2);
          const labelY = branch.y + branch.nodeRadius + 16;
          const handleScenarioClick = () => {
            const allLeaves = branch.children.flatMap(v => v.leaves);
            const points = [
              { x: branch.x - 30, y: branch.y - 30 },
              { x: branch.x + 30, y: branch.y + 30 },
              ...branch.children.map(v => ({ x: v.x - 30, y: v.y - 30 })),
              ...branch.children.map(v => ({ x: v.x + 30, y: v.y + 30 })),
              ...allLeaves.map(l => ({ x: l.x - 60, y: l.y - 20 })),
              ...allLeaves.map(l => ({ x: l.x + 60, y: l.y + 40 })),
            ];
            zoomToPoints(points, `sc-${i}`);
          };
          return (
            <g key={`sc-${i}`} style={{ cursor: 'pointer' }} onClick={handleScenarioClick}>
              <circle cx={branch.x} cy={branch.y} r={branch.nodeRadius}
                fill={branch.color} fillOpacity={0.85} stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} />
              <text x={branch.x} y={branch.y} textAnchor="middle" dominantBaseline="central"
                fill="white" fontSize={Math.max(10, branch.nodeRadius * 0.5) + 'px'} fontWeight="700">
                {branch.totalMembers}
              </text>
              <text x={branch.x} y={labelY} textAnchor="middle" fill="white" fontSize="11px" fontWeight="600">
                {lines.map((line, idx) => (
                  <tspan key={idx} x={branch.x} dy={idx === 0 ? 0 : 13}>{line}</tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* Center node */}
        <g style={{ cursor: 'pointer' }} onClick={onInfoClick} filter="url(#center-glow)">
          <circle cx={layout.cx} cy={layout.cy} r={layout.centerRadius}
            fill={centerFill} fillOpacity={0.9} stroke="rgba(255,255,255,0.6)" strokeWidth={2} />
          <text x={layout.cx} y={layout.cy} textAnchor="middle" dominantBaseline="central"
            fill="white" fontSize={Math.max(14, layout.centerRadius * 0.45) + 'px'} fontWeight="700">
            {memberNodes?.length || 0}
          </text>
          {(() => {
            const { lines: titleLines } = wrapText(pivotPoint?.text || pivotPoint?.title || '', 22, 4);
            const startY = layout.cy + layout.centerRadius + 18;
            return (
              <text x={layout.cx} y={startY} textAnchor="middle" fill="white" fontSize="13px" fontWeight="700">
                {titleLines.map((line, idx) => (
                  <tspan key={idx} x={layout.cx} dy={idx === 0 ? 0 : 15}>{line}</tspan>
                ))}
              </text>
            );
          })()}
        </g>

        </g>
      </svg>

      <ZoomControlsPanel>
        <ZoomButton onClick={handleZoomIn} title={t('pivotPoints.common.zoomIn')}>+</ZoomButton>
        <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
        <ZoomButton onClick={handleZoomOut} title={t('pivotPoints.common.zoomOut')}>−</ZoomButton>
        <ZoomButton onClick={handleZoomReset} title={t('pivotPoints.common.zoomReset')} style={{ fontSize: '0.9rem' }}>⟲</ZoomButton>
      </ZoomControlsPanel>

      {tooltip && (
        <TooltipBox style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-title">{tooltip.title}</div>
          {tooltip.scenarioName && (
            <span className="tooltip-scenario" style={{
              background: `${tooltip.scenarioColor}33`, color: tooltip.scenarioColor,
              border: `1px solid ${tooltip.scenarioColor}66`,
            }}>{tooltip.scenarioName}</span>
          )}
          {tooltip.participant && <div className="tooltip-meta">{tooltip.participant}</div>}
        </TooltipBox>
      )}
    </Container>
  );
}
